import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ListBulletIcon,
  BellAlertIcon,
} from '@heroicons/react/24/outline';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import { generalApi } from '../../api/generalApi';
import Loading from '../../components/ui/Loading';

const CHART_COLORS = ['#3498DB', '#27AE60', '#F39C12', '#E74C3C', '#9B59B6', '#1ABC9C', '#E67E22', '#95A5A6'];

const LicenseDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [expiring, setExpiring] = useState(null);
  const [recentLicenses, setRecentLicenses] = useState([]);
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const [statsRes, expiringRes, licensesRes, chartRes] = await Promise.all([
          generalApi.getDashboardStats(),
          generalApi.getExpiringLicenses(),
          generalApi.getLicenses({ ordering: '-created_at' }),
          generalApi.getChartData(),
        ]);
        if (statsRes?.success || statsRes?.total !== undefined) setStats(statsRes);
        if (expiringRes?.success || expiringRes?.expiring_30 !== undefined) setExpiring(expiringRes);
        const licData = Array.isArray(licensesRes) ? licensesRes : (licensesRes?.data || licensesRes?.results || []);
        setRecentLicenses(licData.slice(0, 5));
        if (chartRes?.by_department !== undefined || chartRes?.by_status !== undefined) setChartData(chartRes);
      } catch (e) {
        setError(e.message || 'Veri yüklenemedi');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return <Loading fullScreen={false} message="Yükleniyor..." />;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Lisans Yönetimi
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Lisans takibi, süre yönetimi ve hatırlatıcılar
          </p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300">
            {error}
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow">
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Toplam</h3>
            <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
              {stats?.total ?? 0}
            </p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow">
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Aktif</h3>
            <p className="text-2xl font-bold text-green-600 dark:text-green-400 mt-1">
              {stats?.active ?? 0}
            </p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow">
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">30 Gün İçinde Dolacak</h3>
            <p className="text-2xl font-bold text-amber-600 dark:text-amber-400 mt-1">
              {stats?.expiring_soon ?? 0}
            </p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow">
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Süresi Dolmuş</h3>
            <p className="text-2xl font-bold text-red-600 dark:text-red-400 mt-1">
              {stats?.expired ?? 0}
            </p>
          </div>
        </div>

        {/* Grafikler */}
        {chartData && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Departman Bazlı Lisans */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Departman Bazlı Lisans Dağılımı</h3>
              <div className="h-64">
                {chartData.by_department?.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData.by_department} layout="vertical" margin={{ top: 5, right: 30, left: 80, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} stroke="#374151" opacity={0.3} />
                      <XAxis type="number" tick={{ fill: '#9CA3AF', fontSize: 11 }} />
                      <YAxis dataKey="name" type="category" width={75} tick={{ fill: '#9CA3AF', fontSize: 11 }} />
                      <Tooltip
                        contentStyle={{ backgroundColor: '#1F2937', border: 'none', borderRadius: '8px', color: '#fff' }}
                        formatter={(value) => [`${value} lisans`, '']}
                      />
                      <Bar dataKey="count" fill="#3498DB" radius={[0, 4, 4, 0]} barSize={20}>
                        {chartData.by_department.map((_, i) => (
                          <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400 text-sm">Veri yok</div>
                )}
              </div>
            </div>

            {/* Durum Bazlı Dağılım */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Durum Bazlı Dağılım</h3>
              <div className="h-64">
                {chartData.by_status?.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={chartData.by_status}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={90}
                        paddingAngle={3}
                        dataKey="value"
                        nameKey="name"
                      >
                        {chartData.by_status.map((_, i) => (
                          <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{ backgroundColor: '#1F2937', border: 'none', borderRadius: '8px', color: '#fff' }}
                        formatter={(value) => [`${value} lisans`, '']}
                      />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400 text-sm">Veri yok</div>
                )}
              </div>
            </div>

            {/* Lisans Tipi Bazlı */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Lisans Tipi Dağılımı</h3>
              <div className="h-64">
                {chartData.by_type?.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData.by_type} margin={{ top: 5, right: 30, left: 20, bottom: 30 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#374151" opacity={0.3} />
                      <XAxis dataKey="name" tick={{ fill: '#9CA3AF', fontSize: 10 }} angle={-25} textAnchor="end" height={60} />
                      <YAxis tick={{ fill: '#9CA3AF', fontSize: 11 }} />
                      <Tooltip
                        contentStyle={{ backgroundColor: '#1F2937', border: 'none', borderRadius: '8px', color: '#fff' }}
                        formatter={(value) => [`${value} lisans`, '']}
                      />
                      <Bar dataKey="count" fill="#3498DB" radius={[4, 4, 0, 0]} barSize={28}>
                        {chartData.by_type.map((_, i) => (
                          <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400 text-sm">Veri yok</div>
                )}
              </div>
            </div>

            {/* Önümüzdeki 6 Ay Dolacak Lisanslar */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Önümüzdeki 6 Ay Dolacak Lisanslar</h3>
              <div className="h-64">
                {chartData.expiring_by_month?.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData.expiring_by_month} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#374151" opacity={0.3} />
                      <XAxis dataKey="name" tick={{ fill: '#9CA3AF', fontSize: 11 }} />
                      <YAxis tick={{ fill: '#9CA3AF', fontSize: 11 }} />
                      <Tooltip
                        contentStyle={{ backgroundColor: '#1F2937', border: 'none', borderRadius: '8px', color: '#fff' }}
                        formatter={(value) => [`${value} lisans`, '']}
                      />
                      <Bar dataKey="count" fill="#F39C12" radius={[4, 4, 0, 0]} barSize={28} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400 text-sm">Veri yok</div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Son Eklenen Lisanslar */}
        {recentLicenses.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-8">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                <ListBulletIcon className="h-5 w-5 mr-2 text-gray-500 dark:text-gray-400" />
                Son Eklenen Lisanslar
              </h2>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead>
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Lisans</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Tip</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Otel</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Bitiş Tarihi</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Durum</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {recentLicenses.map((lic) => (
                    <tr
                      key={lic.id}
                      onClick={() => navigate(`/general/licenses/list?highlight=${lic.id}`)}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
                    >
                      <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-white">{lic.name}</td>
                      <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">{lic.license_type_name}</td>
                      <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">{lic.hotel_name || 'Global'}</td>
                      <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">{lic.expiry_date}</td>
                      <td>
                        <span className={`px-2 py-1 text-xs rounded ${
                          lic.status === 'active' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' :
                          lic.status === 'expired' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' :
                          'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                        }`}>
                          {lic.status === 'active' ? 'Aktif' : lic.status === 'expired' ? 'Süresi Dolmuş' : lic.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Expiring Licenses Table */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
              <BellAlertIcon className="h-5 w-5 mr-2 text-amber-500" />
              Yaklaşan ve Süresi Dolan Lisanslar
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead>
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Lisans</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Tip</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Bitiş Tarihi</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Durum</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {expiring?.expired?.length > 0 &&
                  expiring.expired.map((lic) => (
                    <tr
                      key={lic.id}
                      onClick={() => navigate(`/general/licenses/list?highlight=${lic.id}`)}
                      className="bg-red-50/50 dark:bg-red-900/10 cursor-pointer hover:bg-red-50 dark:hover:bg-red-900/20"
                    >
                      <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-white">{lic.name}</td>
                      <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">{lic.license_type_name}</td>
                      <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">{lic.expiry_date}</td>
                      <td><span className="px-2 py-1 text-xs rounded bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300">Süresi Dolmuş</span></td>
                    </tr>
                  ))}
                {expiring?.expiring_7?.length > 0 &&
                  expiring.expiring_7.map((lic) => (
                    <tr
                      key={lic.id}
                      onClick={() => navigate(`/general/licenses/list?highlight=${lic.id}`)}
                      className="bg-amber-50/50 dark:bg-amber-900/10 cursor-pointer hover:bg-amber-50 dark:hover:bg-amber-900/20"
                    >
                      <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-white">{lic.name}</td>
                      <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">{lic.license_type_name}</td>
                      <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">{lic.expiry_date}</td>
                      <td><span className="px-2 py-1 text-xs rounded bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">7 gün içinde</span></td>
                    </tr>
                  ))}
                {expiring?.expiring_14?.length > 0 &&
                  expiring.expiring_14.filter((l) => !expiring?.expiring_7?.some((e7) => e7.id === l.id)).map((lic) => (
                    <tr
                      key={lic.id}
                      onClick={() => navigate(`/general/licenses/list?highlight=${lic.id}`)}
                      className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50"
                    >
                      <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-white">{lic.name}</td>
                      <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">{lic.license_type_name}</td>
                      <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">{lic.expiry_date}</td>
                      <td><span className="px-2 py-1 text-xs rounded bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300">14 gün içinde</span></td>
                    </tr>
                  ))}
                {expiring?.expiring_30?.length > 0 &&
                  expiring.expiring_30
                    .filter((l) => !expiring?.expiring_14?.some((e14) => e14.id === l.id) && !expiring?.expiring_7?.some((e7) => e7.id === l.id))
                    .slice(0, 5)
                    .map((lic) => (
                      <tr
                        key={lic.id}
                        onClick={() => navigate(`/general/licenses/list?highlight=${lic.id}`)}
                        className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50"
                      >
                        <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-white">{lic.name}</td>
                        <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">{lic.license_type_name}</td>
                        <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">{lic.expiry_date}</td>
                        <td><span className="px-2 py-1 text-xs rounded bg-highlight-100 text-highlight-800 dark:bg-highlight-500/20 dark:text-highlight-300">30 gün içinde</span></td>
                      </tr>
                    ))}
                {(!expiring?.expired?.length && !expiring?.expiring_7?.length && !expiring?.expiring_14?.length && !expiring?.expiring_30?.length) && (
                  <tr>
                    <td colSpan={4} className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                      Yaklaşan veya süresi dolan lisans bulunmuyor.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LicenseDashboard;
